import { memo } from 'react'
import { AlertTriangle, Ban, CalendarClock, Clock3, TrendingUp } from 'lucide-react'
import { formatCurrencyCompact, formatPercent } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import { Tooltip } from '@/components/ui/Tooltip'
import { getDealAvatarFallback } from '@/utils/dealDisplay'
import { KanbanMoveStageMenu } from './KanbanMoveStageMenu'
import type { Deal, DealStage } from '@/types'

export type KanbanCardProps = {
  deal: Deal
  stageColor: string
  onClick: () => void
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  isDragging?: boolean
}

function KanbanCardComponent({
  deal,
  stageColor,
  onClick,
  onMoveStage,
  isDragging,
}: KanbanCardProps) {
  const risk = getDealRisk(deal)
  const nextStep = getNextStep(deal)
  const isRedundantName = deal.nome.toLowerCase().includes(deal.empresaNome.toLowerCase())

  return (
    <div
      onClick={onClick}
      className={cn(
        'group/card relative flex flex-col gap-3.5 overflow-hidden rounded-xl border border-border bg-card p-4 shadow-sm',
        'cursor-pointer transition-all duration-300 hover:border-accent/50 hover:shadow-lg hover:shadow-accent/5',
        risk.tone === 'warning' && 'border-warning/30 bg-warning/[0.015]',
        risk.tone === 'danger' && 'border-danger/30 bg-danger/[0.015]',
        isDragging && 'scale-95 border-accent opacity-60 ring-2 ring-accent/20',
      )}
    >
      <div
        className="absolute left-0 right-0 top-0 h-1 opacity-80"
        style={{ background: `linear-gradient(90deg, ${stageColor}99, ${stageColor})` }}
      />

      <div className="mt-0.5 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold tracking-tight text-text-primary">
            {deal.empresaNome}
          </p>
          {!isRedundantName && (
            <p className="mt-0.5 truncate text-[11px] text-text-muted">{deal.nome}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {onMoveStage && <KanbanMoveStageMenu deal={deal} onMoveStage={onMoveStage} />}
          {risk.icon && (
            <Tooltip content={risk.label} position="bottom">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-md border shadow-sm',
                  risk.tone === 'danger'
                    ? 'border-danger/20 bg-danger/10 text-danger'
                    : 'border-warning/20 bg-warning/10 text-warning',
                )}
              >
                {risk.icon}
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-text-muted">
            Valor Previsto
          </span>
          <p className="text-base font-bold leading-none tracking-tight text-text-primary">
            {formatCurrencyCompact(deal.valor)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-[11px] font-bold tabular-nums text-text-primary">
            {formatPercent(deal.probabilidade, 0)}
          </span>
          <div className="h-1.5 w-16 overflow-hidden rounded-full border border-white/5 bg-black/20">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${deal.probabilidade}%`,
                background: `linear-gradient(90deg, ${stageColor}80, ${stageColor})`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 border-t border-border/50 pt-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-white/5 bg-black/20 px-2 py-1.5">
            <CalendarClock size={11} className="shrink-0 text-accent" />
            <p className="truncate text-[10px] font-medium text-text-secondary">{nextStep}</p>
          </div>
          <Tooltip content={`Closer: ${deal.closerNome || 'Não atribuído'}`} position="bottom">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-accent/20 bg-accent/10">
              <span className="text-[9px] font-bold uppercase text-accent">
                {getDealAvatarFallback(deal.closerNome, deal.closerAvatar)}
              </span>
            </div>
          </Tooltip>
        </div>
        <div className="flex items-center justify-between text-[10px] font-medium">
          <div className="flex items-center gap-1 text-text-muted">
            <Clock3 size={10} />
            <span>{deal.driftDays > 0 ? `${deal.driftDays}d etapa` : 'Novo'}</span>
          </div>
          {(risk.label || deal.motivoPerda) && (
            <span
              className={cn(
                'max-w-[100px] truncate',
                risk.tone === 'danger' ? 'text-danger' : 'text-warning',
              )}
            >
              {deal.motivoPerda || risk.label}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export const KanbanCard = memo(KanbanCardComponent)

function isSlaBreached(deal: Deal) {
  if (deal.stagnant) return true
  if (deal.etapa === 'contrato_enviado' && deal.driftDays >= 3) return true
  if (deal.etapa === 'proposta_enviada' && deal.driftDays >= 5) return true
  return deal.driftDays >= 7
}

function getDealRisk(deal: Deal): {
  label: string
  tone: 'warning' | 'danger'
  icon: React.ReactNode
} {
  if (deal.motivoPerda) {
    return { label: 'Perda registrada', tone: 'danger', icon: <Ban size={14} /> }
  }
  if (isDateOverdue(deal.dataFechamentoPrevisto)) {
    return { label: 'Fechamento previsto vencido', tone: 'danger', icon: <Clock3 size={14} /> }
  }
  if (deal.objecoes) {
    return { label: 'Objeção registrada', tone: 'warning', icon: <AlertTriangle size={14} /> }
  }
  if (isSlaBreached(deal)) {
    return { label: `${deal.driftDays}d sem avanço`, tone: 'warning', icon: <Clock3 size={14} /> }
  }
  if (
    deal.probabilidade < 45 &&
    ['proposta_enviada', 'renegociacao', 'gerar_contrato'].includes(deal.etapa)
  ) {
    return {
      label: 'Probabilidade baixa para etapa',
      tone: 'warning',
      icon: <TrendingUp size={14} />,
    }
  }
  return { label: '', tone: 'warning', icon: null }
}

function getNextStep(deal: Deal) {
  const steps: Partial<Record<DealStage, string>> = {
    qualificado: 'Agendar reunião',
    reuniao_agendada: deal.dataReuniao ? 'Realizar reunião' : 'Confirmar agenda',
    reuniao_realizada: 'Elaborar proposta',
    proposta_elaboracao: 'Enviar proposta',
    proposta_enviada: 'Fazer follow-up',
    teste_gratis: 'Retomar após o teste',
    renegociacao: 'Resolver objeções',
    gerar_contrato: 'Gerar contrato',
    contrato_enviado: 'Coletar assinatura',
    ganho: 'Ativar pós-venda',
    perdido: 'Registrar aprendizado',
    pausado: 'Definir retomada',
    fechamento_futuro: 'Programar contato',
  }
  return steps[deal.etapa] ?? 'Atualizar etapa'
}

function isDateOverdue(date?: string) {
  if (!date) return false
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  parsed.setHours(0, 0, 0, 0)
  return parsed < today
}
